import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  CircularProgress,
} from "@mui/material";
import { useForm, Controller } from "react-hook-form";
import CustomRankingInput from "../../components/formComponent/CustomRankingInput";
import { addRanking, editRanking } from "../../services/rankingsApi";
import moment from "moment";

// Parse date to get month and year
const parseDate = (date: string) => {
  const parsedDate = moment(date);
  return {
    month: parsedDate.month() + 1, // moment months are 0-based
    year: parsedDate.year(),
  };
};

type AddRankingModalProps = {
  open: boolean;
  onClose: () => void;
  onSave: (data: RankingFormData) => void;
  clinicData: any;
  dateFilter: {
    startDate: string;
    endDate: string;
  };
  mode: any;
  initialData?: any;
};

type RankingFormData = {
  keyword: string;
  currentRanking: number;
  previousRanking: number;
};

const AddRankingModal: React.FC<AddRankingModalProps> = ({
  open,
  onClose,
  onSave,
  clinicData,
  dateFilter,
  mode,
  initialData,
}) => {
  console.log(mode, "mode");
  const { handleSubmit, control, reset, getValues, watch } =
    useForm<RankingFormData>({
      defaultValues: {
        keyword: initialData?.keyword,
        currentRanking: Number(initialData?.current),
        previousRanking: Number(initialData?.previous),
      },
    });

  useEffect(() => {
    if (mode === "edit" && initialData) {
      console.log(initialData, "initialData");
      reset({
        keyword: initialData.keyword ?? "",
        currentRanking: Number(initialData.current) ?? 1,
        previousRanking: Number(initialData.previous) ?? 1,
      });
    } else if (mode !== "edit") {
      reset({
        keyword: "",
        currentRanking: 1,
        previousRanking: 1,
      });
    }
  }, [initialData, mode, reset]);

  const allValues = getValues();
  console.log("allValues", watch());
  const [isLoading, setIsLoading] = useState(false);

  const handleSave = async (data: RankingFormData) => {
    try {
      setIsLoading(true);
      const endDate = parseDate(dateFilter.endDate);
      const payload = {
        month: endDate.month.toString(),
        year: endDate.year.toString(),
        keyword: data.keyword,
        current: data.currentRanking.toString(),
        previous: data.previousRanking.toString(),
        ...(mode === "edit" ? { id: initialData.id } : {}),
      };

      let response;
      if (mode == "edit") {
        response = await editRanking(payload, clinicData);
      } else {
        response = await addRanking(payload, clinicData);
      }

      if (response?.status?.msg === "Success") {
        onSave(data);
        onClose();
        reset();
      }
    } catch (error) {
      console.error("Error saving ranking:", error);
      // You might want to show an error message to the user here
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    onClose();
    reset(); // Reset the form when closing
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth={true}
      maxWidth="sm"
      PaperProps={{
        style: { borderRadius: "10px" },
      }}
    >
      <DialogTitle
        sx={{
          backgroundColor: "#194e5b",
          color: "white",
          borderTopLeftRadius: "10px",
          borderTopRightRadius: "10px",
        }}
      >
        {mode === "edit" ? "Edit Keyword Ranking" : "Add New Keyword Ranking"}
      </DialogTitle>
      <DialogContent sx={{ marginTop: "10px" }}>
        <Controller
          name="keyword"
          control={control}
          rules={{ required: "Keyword is required" }}
          render={({
            field: { onChange, onBlur, value, name, ref },
            fieldState,
          }) => (
            <CustomRankingInput
              name="keyword"
              label="Keyword"
              placeholder="e.g. Dentist Near Me"
              field={{ onChange, onBlur, value, name, ref }}
              fieldState={fieldState}
            />
          )}
        />
        <div className="mt-3">
          <Controller
            name="currentRanking"
            control={control}
            rules={{
              required: "Current Ranking Position is required",
              min: 0,
              validate: (value) =>
                value >= 0 || "Position must be greater than or equal to 0",
            }}
            render={({ field, fieldState: { error } }) => (
              <CustomRankingInput
                name="currentRanking"
                label="Current Ranking Position"
                type="number"
                helperText="Lower number means better position"
                field={field}
                fieldState={
                  {
                    error: error
                      ? { type: error.type, message: error.message }
                      : undefined,
                  } as any
                } // Explicitly cast to any
              />
            )}
          />
        </div>
        <Controller
          name="previousRanking"
          control={control}
          rules={{
            required: "Previous Ranking Position is required",
            min: 0,
            validate: (value) =>
              value >= 0 || "Position must be greater than or equal to 0",
          }}
          render={({ field, fieldState: { error } }) => (
            <CustomRankingInput
              label="Baseline Ranking Position"
              type="number"
              helperText="The position from the previous reporting period"
              field={field}
              fieldState={
                {
                  error: error
                    ? { type: error.type, message: error.message }
                    : undefined,
                } as any
              } // Explicitly cast to any
            />
          )}
        />
      </DialogContent>
      <DialogActions>
        <button
          onClick={handleClose}
          className="btn btn-sm"
          style={{ backgroundColor: "#c9eeeb", color: "#0E3843" }}
        >
          Cancel
        </button>
        <button
          onClick={handleSubmit(handleSave)}
          style={{ backgroundColor: "#0E3843" }}
          className="btn btn-sm bg-[#0E3843] rounded-md text-white border hover:bg-[#0A2A30]"
          disabled={isLoading}
        >
          {isLoading ? (
            <CircularProgress size={20} color="inherit" />
          ) : mode === "edit" ? (
            "Update Ranking"
          ) : (
            "Save Ranking"
          )}
        </button>
      </DialogActions>
    </Dialog>
  );
};

export default AddRankingModal;
